import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Header } from '../../components/layout/Header';
import { createProject } from '../../lib/api/projects';
import { useAuth } from '../../lib/auth/AuthContext';
import { PROJECT_ID_PATTERN } from '../../../../shared/types/projects';

/**
 * /projects/new — create-project form.
 *
 * Fields: id (slug, validated client-side against PROJECT_ID_PATTERN),
 * name (optional, defaults to id), plan (optional dropdown — four-plan model).
 * On success, navigates to /projects/:id.
 */
export const NewProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentAccount } = useAuth();

  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const idTrimmed = id.trim();
  const idEmpty = submitted && idTrimmed === '';
  const idMalformed =
    submitted && idTrimmed !== '' && !PROJECT_ID_PATTERN.test(idTrimmed);

  const mutation = useMutation({
    mutationFn: createProject,
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      navigate(`/projects/${encodeURIComponent(project.id)}`, { replace: true });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (idTrimmed === '' || !PROJECT_ID_PATTERN.test(idTrimmed)) return;
    mutation.mutate({
      id: idTrimmed,
      ...(name.trim() ? { name: name.trim() } : {}),
      // Plan is workspace-scoped (one plan per workspace covers all its
      // projects), so a project no longer carries its own plan.
      // Create under the active workspace so the project lands in the
      // account the operator is currently looking at.
      ...(currentAccount ? { accountId: currentAccount.id } : {}),
    });
  };

  const submitting = mutation.isPending;
  const apiError = mutation.error ? (mutation.error as Error).message : null;

  return (
    <div data-testid="new-project-page" className="min-h-screen bg-white">
      <Header title="New Project" />
      <main className="max-w-xl mx-auto px-4 py-6">
        <Link to="/projects" className="text-sm text-blue-600 hover:underline">
          ← Back to projects
        </Link>

        {currentAccount && (
          <p className="mt-3 text-xs text-slate-500" data-testid="new-project-account">
            Creating in workspace{' '}
            <span className="font-medium text-slate-700">{currentAccount.name}</span>. Switch
            workspaces from the account menu in the header.
          </p>
        )}

        <form
          onSubmit={handleSubmit}
          aria-label="Create a new project"
          className="mt-4 space-y-4 border rounded p-4 bg-white shadow-sm"
          noValidate
        >
          <div className="space-y-1">
            <label htmlFor="project-id" className="block text-sm font-medium">
              Project id (slug)
            </label>
            <input
              id="project-id"
              type="text"
              value={id}
              onChange={(e) => setId(e.target.value.toLowerCase())}
              placeholder="acme-platform"
              autoComplete="off"
              autoFocus
              required
              disabled={submitting}
              aria-invalid={idEmpty || idMalformed || undefined}
              aria-describedby={
                idEmpty
                  ? 'id-empty-error'
                  : idMalformed
                  ? 'id-format-error'
                  : 'id-hint'
              }
              className="block w-full border rounded px-2 py-1 text-sm font-mono"
            />
            {idEmpty && (
              <p id="id-empty-error" className="text-xs text-red-600">
                Project id is required
              </p>
            )}
            {idMalformed && (
              <p id="id-format-error" className="text-xs text-red-600">
                Lowercase letters, numbers and dashes only; cannot start or
                end with a dash; max 63 chars.
              </p>
            )}
            {!idEmpty && !idMalformed && (
              <p id="id-hint" className="text-xs text-gray-500">
                Used in URLs and DynamoDB keys. Cannot be changed later.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label htmlFor="project-name" className="block text-sm font-medium">
              Display name <span className="text-gray-400">(optional)</span>
            </label>
            <input
              id="project-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Platform"
              autoComplete="off"
              disabled={submitting}
              maxLength={120}
              className="block w-full border rounded px-2 py-1 text-sm"
            />
            <p className="text-xs text-gray-500">
              Defaults to the project id if left blank.
            </p>
          </div>

          <div className="space-y-1">
            <span className="block text-sm font-medium">Plan</span>
            <p className="text-xs text-gray-500">
              Plan is set once per workspace and covers every project in it — repo limits,
              deploy stages, stacks, and model providers all come from the workspace plan.{' '}
              <Link to="/account/plan" className="text-blue-600 hover:underline">
                Manage the workspace plan
              </Link>
              .
            </p>
          </div>

          {apiError && (
            <div role="alert" className="text-sm text-red-600">
              {apiError}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 text-white text-sm py-1.5 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700"
            >
              {submitting ? 'Creating…' : 'Create project'}
            </button>
            <Link
              to="/projects"
              className="text-sm py-1.5 px-4 border rounded hover:bg-gray-50"
            >
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
};

export default NewProjectPage;
